import { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2 } from 'lucide-react';
import type { JobFinancials, JobPayment } from './CompleteJobDialog';

const paymentSchema = z.object({
  payments: z.array(
    z.object({
      amount: z.number().min(0, 'Amount must be 0 or greater'),
      payment_method: z.string().min(1, 'Payment method is required'),
      payment_date: z.string(),
      notes: z.string().optional(),
    })
  ),
});

type PaymentFormData = {
  payments: Array<{
    amount: number;
    payment_method: string;
    payment_date: string;
    notes?: string;
  }>;
};

interface CompleteJobPaymentsStepProps {
  financials: JobFinancials;
  initialPayments: JobPayment[];
  onNext: (payments: JobPayment[]) => void;
  onBack: () => void;
  onCancel: () => void;
}

export function CompleteJobPaymentsStep({ financials, initialPayments, onNext, onBack, onCancel }: CompleteJobPaymentsStepProps) {
  const { formatDateInput, parseDate } = useTimezoneUtils();
  const { register, control, handleSubmit, watch, formState: { errors } } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      // Start empty — staff add payments only if money was collected
      payments: initialPayments.length > 0
        ? initialPayments.map(p => {
          let dateStr: string;
          const paymentDate: any = p.payment_date;

          if (paymentDate instanceof Date && !isNaN(paymentDate.getTime())) {
            dateStr = formatDateInput(paymentDate);
          } else if (typeof paymentDate === 'string') {
            dateStr = paymentDate.split('T')[0];
          } else {
            dateStr = formatDateInput(new Date());
          }

          return {
            amount: p.amount,
            payment_method: p.payment_method,
            payment_date: dateStr,
            notes: p.notes,
          };
        })
        : [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'payments',
  });

  const paymentsData = watch('payments');
  const totalPaid = paymentsData.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const revenue = financials.invoice_total;

  const getPaymentStatus = () => {
    const actualPaid = paymentsData.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    if (actualPaid === 0) return { label: 'UNPAID', color: 'bg-destructive' };
    if (actualPaid >= revenue) return { label: 'PAID', color: 'bg-success' };
    return { label: 'PARTIALLY PAID', color: 'bg-warning' };
  };

  const paymentStatus = getPaymentStatus();

  const onSubmit = (data: PaymentFormData) => {
    // Strip any $0 rows — staff may add a row then not fill it in
    const payments: JobPayment[] = data.payments
      .filter(p => p.amount > 0)
      .map(p => ({
        amount: p.amount,
        payment_method: p.payment_method,
        // Midnight in the company timezone (new Date('yyyy-MM-dd') would be midnight UTC).
        payment_date: parseDate(p.payment_date),
        notes: p.notes,
      }));
    onNext(payments);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Step 2: Record Payments</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add payments received today. If no money was collected, click <strong>Next</strong> — the job will be marked Unpaid automatically.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => append({ amount: 0, payment_method: 'cash', payment_date: formatDateInput(new Date()), notes: '' })}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Payment
          </Button>
        </div>

        <Card className="p-4 bg-muted/50">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-muted-foreground">Total Job Revenue</p>
              <p className="text-2xl font-bold">${revenue.toFixed(2)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total Payments</p>
              <p className="text-2xl font-bold">${totalPaid.toFixed(2)}</p>
            </div>
            <Badge className={paymentStatus.color} variant="default">
              {paymentStatus.label}
            </Badge>
          </div>
          {totalPaid > revenue && (
            <p className="text-sm text-warning mt-2">⚠️ Payment total exceeds revenue</p>
          )}
        </Card>

        <div className="space-y-4">
          {fields.map((field, index) => (
            <Card key={field.id} className="p-4">
              <div className="flex items-start justify-between mb-4">
                <h4 className="font-medium">Payment {index + 1}</h4>
                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(index)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`amount-${index}`}>Amount *</Label>
                  <Input
                    id={`amount-${index}`}
                    type="number"
                    step="0.01"
                    {...register(`payments.${index}.amount`, { valueAsNumber: true })}
                    className="mt-1"
                  />
                  {errors.payments?.[index]?.amount && (
                    <p className="text-sm text-destructive mt-1">{errors.payments[index]?.amount?.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor={`method-${index}`}>Payment Method *</Label>
                  <Select
                    value={paymentsData[index]?.payment_method}
                    onValueChange={(value) => {
                      const currentPayments = [...paymentsData];
                      currentPayments[index].payment_method = value;
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="card">Card / EFTPOS</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <input type="hidden" {...register(`payments.${index}.payment_method`)} />
                </div>

                <div>
                  <Label htmlFor={`date-${index}`}>Payment Date</Label>
                  <Input
                    id={`date-${index}`}
                    type="date"
                    {...register(`payments.${index}.payment_date`)}
                    className="mt-1"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor={`notes-${index}`}>Notes</Label>
                  <Textarea
                    id={`notes-${index}`}
                    {...register(`payments.${index}.notes`)}
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {errors.payments?.root && (
          <p className="text-sm text-destructive">{errors.payments.root.message}</p>
        )}
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            Next: Review & Complete
          </Button>
        </div>
      </div>
    </form>
  );
}
